import React, { useState, useEffect } from 'react';
import { Check, X, Trash2, Star } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const AdminReviews = () => {
  const { authHeader } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    axios.get(`${API}/reviews`, { headers: authHeader })
      .then(r => setReviews(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const handleApprove = async (id, approved) => {
    try {
      const { data } = await axios.put(`${API}/reviews/${id}`, { isApproved: approved }, { headers: authHeader });
      setReviews(prev => prev.map(r => r._id === id ? { ...r, isApproved: data.isApproved } : r));
      toast.success(approved ? 'Review approved' : 'Review rejected');
    } catch { toast.error('Failed to update review'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this review?')) return;
    try {
      await axios.delete(`${API}/reviews/${id}`, { headers: authHeader });
      setReviews(prev => prev.filter(r => r._id !== id));
      toast.success('Review deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const filtered = reviews.filter(r => {
    if (filter === 'pending') return !r.isApproved;
    if (filter === 'approved') return r.isApproved;
    return true;
  });

  return (
    <div className="p-6" data-testid="admin-reviews">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-playfair text-2xl font-bold text-[#1F1410]">Reviews</h1>
          <p className="text-[#7A6A63] text-sm">{reviews.filter(r => !r.isApproved).length} pending approval</p>
        </div>
        <div className="flex border border-[#ECE0DA] rounded-full overflow-hidden">
          {[['all', 'All'], ['pending', 'Pending'], ['approved', 'Approved']].map(([val, label]) => (
            <button key={val} onClick={() => setFilter(val)}
              className={`px-4 py-2 text-sm font-medium transition-colors ${filter === val ? 'bg-[#7A1125] text-white' : 'text-[#7A6A63] hover:bg-[#FDF8F3]'}`}
              data-testid={`reviews-filter-${val}`}>
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-3">
        {loading ? (
          <div className="flex justify-center py-12"><div className="w-8 h-8 border-2 border-[#7A1125] border-t-transparent rounded-full animate-spin" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-[#7A6A63]">No reviews in this category</div>
        ) : filtered.map(r => (
          <div key={r._id} className={`bg-white rounded-xl border p-4 flex items-start gap-4 shadow-sm ${!r.isApproved ? 'border-orange-200 bg-orange-50/30' : 'border-[#ECE0DA]'}`} data-testid={`review-row-${r._id}`}>
            {r.productId?.images?.[0] && (
              <img src={r.productId.images[0]} alt={r.productId.name} className="w-12 h-12 rounded-lg object-cover border border-[#ECE0DA] shrink-0" onError={e => { e.target.style.display = 'none'; }} />
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="font-semibold text-[#1F1410] text-sm">{r.customerName}</p>
                  <p className="text-xs text-[#7A6A63]">{r.productId?.name || 'Unknown Product'}</p>
                </div>
                <div className="flex items-center gap-0.5 shrink-0">
                  {[1,2,3,4,5].map(s => <Star key={s} size={12} fill={s<=r.rating?'#F59E0B':'none'} stroke={s<=r.rating?'#F59E0B':'#D1C4BB'} />)}
                </div>
              </div>
              {r.comment && <p className="text-sm text-[#7A6A63] mt-1 line-clamp-2">{r.comment}</p>}
              <div className="flex items-center gap-3 mt-2">
                <span className="text-xs text-[#7A6A63]">{new Date(r.createdAt).toLocaleDateString('en-IN')}</span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${r.isApproved ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-orange-50 text-orange-700 border border-orange-200'}`}>
                  {r.isApproved ? 'Approved' : 'Pending'}
                </span>
              </div>
            </div>
            <div className="flex gap-1.5 shrink-0">
              {!r.isApproved && (
                <button onClick={() => handleApprove(r._id, true)} className="p-2 bg-green-50 hover:bg-green-100 text-green-600 rounded-full transition-colors" data-testid={`approve-review-${r._id}`}>
                  <Check size={14} />
                </button>
              )}
              {r.isApproved && (
                <button onClick={() => handleApprove(r._id, false)} className="p-2 bg-orange-50 hover:bg-orange-100 text-orange-600 rounded-full transition-colors" data-testid={`reject-review-${r._id}`}>
                  <X size={14} />
                </button>
              )}
              <button onClick={() => handleDelete(r._id)} className="p-2 bg-red-50 hover:bg-red-100 text-red-500 rounded-full transition-colors" data-testid={`delete-review-${r._id}`}>
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminReviews;
